/**
 * Everything MO OS knows about one person, from the distilled nodes.
 *
 * Deliberately shows the resolution state. When an identity is still pending, the
 * owner is the only one who can settle it, and hiding that uncertainty behind a
 * confident-looking page is how a knowledge system starts lying to you.
 */

import { useEffect, useState } from 'react';
import { dueLabel, fetchPerson, type Dossier, type Loaded } from './api.js';

export function PersonDossier({
  name,
  onBack,
}: {
  name: string;
  onBack: () => void;
}): JSX.Element {
  const [state, setState] = useState<Loaded<Dossier>>({ state: 'loading' });

  useEffect(() => {
    void fetchPerson(name).then(setState);
  }, [name]);

  return (
    <main className="mo-dossier">
      <button type="button" onClick={onBack}>
        Back
      </button>

      {state.state === 'loading' && <p>Loading.</p>}
      {state.state === 'disabled' && <p>{state.message}</p>}
      {state.state === 'error' && <p>Nothing recorded for {name} yet.</p>}

      {state.state === 'ready' && (
        <>
          <h1>{state.value.name}</h1>

          {state.value.resolution === 'pending' && (
            <p className="mo-pending">
              This identity is unconfirmed. MO OS could not tell which known person this is, so
              treat the links below as provisional.
            </p>
          )}

          <dl className="mo-facts">
            {state.value.role !== '' && (
              <>
                <dt>Role</dt>
                <dd>{state.value.role}</dd>
              </>
            )}
            {state.value.vantageId !== '' && (
              <>
                <dt>Known as</dt>
                <dd>{state.value.vantageId} in the vantage brain</dd>
              </>
            )}
            {state.value.emails.length > 0 && (
              <>
                <dt>Email</dt>
                <dd>{state.value.emails.join(', ')}</dd>
              </>
            )}
            {state.value.aliases.length > 0 && (
              <>
                <dt>Also known as</dt>
                <dd>{state.value.aliases.join(', ')}</dd>
              </>
            )}
            {state.value.referredBy !== '' && (
              <>
                <dt>Introduced by</dt>
                <dd>{state.value.referredBy}</dd>
              </>
            )}
          </dl>

          <section>
            <h2>You owe them</h2>
            <CommitmentList items={state.value.owedByOwner} />
          </section>

          <section>
            <h2>They owe you</h2>
            <CommitmentList items={state.value.owedToOwner} />
          </section>

          <section>
            <h2>Engagements</h2>
            {state.value.topics.length === 0 ? (
              <p className="mo-empty">None recorded.</p>
            ) : (
              <ul className="mo-list">
                {state.value.topics.map((t) => (
                  <li key={t.path}>
                    <strong>{t.name}</strong> {t.summary}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h2>Decisions on shared work</h2>
            {state.value.decisions.length === 0 ? (
              <p className="mo-empty">None recorded.</p>
            ) : (
              <ul className="mo-list">
                {state.value.decisions.map((d) => (
                  <li key={d.path}>{d.text}</li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </main>
  );
}

function CommitmentList({
  items,
}: {
  items: { text: string; path: string; due: string; blockedOn: string }[];
}): JSX.Element {
  if (items.length === 0) return <p className="mo-empty">Nothing outstanding.</p>;

  return (
    <ul className="mo-list">
      {items.map((c) => (
        <li key={c.path}>
          {c.text}
          {c.due !== '' && <span className="mo-due"> {dueLabel(c.due)}</span>}
          {c.blockedOn !== '' && <span className="mo-blocked"> blocked on {c.blockedOn}</span>}
        </li>
      ))}
    </ul>
  );
}
